const ART_INIT_ATTR = 'data-art-init';
const instances = new WeakMap();

function hexWithAlpha(hex, alphaHex) {
  const value = String(hex || '#111111').replace('#', '');
  return `#${value}${alphaHex}`;
}

function isOverDrawable(clientX, clientY) {
  const hits = document.elementsFromPoint(clientX, clientY);
  for (let i = 0; i < hits.length; i += 1) {
    const el = hits[i];
    if (!el || !el.closest) continue;
    if (el.closest('#ui-header')) return false;
    if (el.closest('.art-page__tools')) return false;
    if (el.closest('.art-page__line, .art-page__form, .art-page__field, .art-page__input, .art-page__submit, .art-page__msg')) {
      return false;
    }
    if (el.classList.contains('art-page__preview')) continue;
    if (el.classList.contains('art-page__canvas')) return true;
  }
  return false;
}

function initArtPage(root) {
  if (!root || root.getAttribute(ART_INIT_ATTR) === 'true') return;

  const canvas = root.querySelector('.art-page__canvas');
  const preview = root.querySelector('.art-page__preview');
  if (!canvas || !preview) return;

  const ctx = canvas.getContext('2d');
  const pctx = preview.getContext('2d');
  if (!ctx || !pctx) return;

  root.setAttribute(ART_INIT_ATTR, 'true');

  let drawing = false;
  let lastX = 0;
  let lastY = 0;
  let lastPreviewX = null;
  let lastPreviewY = null;
  let color = root.querySelector('.art-page__swatch.is-active')?.dataset.color || '#111111';
  let size = 12;
  let mode = root.querySelector('.art-page__mode.is-active')?.dataset.mode || 'pen';

  const slider = root.querySelector('.art-page__slider');
  if (slider) size = Number(slider.value) || 12;
  const swatches = root.querySelectorAll('.art-page__swatch');
  const modes = root.querySelectorAll('.art-page__mode');
  const clearBtn = root.querySelector('.art-page__clear');

  const point = (e) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const clearPreview = () => {
    lastPreviewX = null;
    lastPreviewY = null;
    pctx.clearRect(0, 0, preview.width, preview.height);
  };

  const drawPreview = (x, y) => {
    lastPreviewX = x;
    lastPreviewY = y;
    pctx.clearRect(0, 0, preview.width, preview.height);
    const ratio = window.devicePixelRatio || 1;
    const radius = Math.max(1, (size / 2) * ratio);
    pctx.beginPath();
    pctx.arc(x, y, radius, 0, Math.PI * 2);
    if (mode !== 'eraser') {
      pctx.fillStyle = hexWithAlpha(color, '2e');
      pctx.fill();
    }
    pctx.strokeStyle = '#000';
    pctx.lineWidth = Math.max(1, 1.5 * ratio);
    pctx.stroke();
  };

  const applyBrush = () => {
    const ratio = window.devicePixelRatio || 1;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(2, size * ratio);
    if (mode === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
    }
    if (lastPreviewX != null && lastPreviewY != null) {
      drawPreview(lastPreviewX, lastPreviewY);
    }
  };

  const updatePreview = (e) => {
    if (!isOverDrawable(e.clientX, e.clientY)) {
      clearPreview();
      return;
    }
    const p = point(e);
    drawPreview(p.x, p.y);
  };

  const resize = () => {
    const ratio = window.devicePixelRatio || 1;
    const rect = root.getBoundingClientRect();
    const nextW = Math.max(1, Math.round(rect.width * ratio));
    const nextH = Math.max(1, Math.round(rect.height * ratio));
    if (canvas.width !== nextW || canvas.height !== nextH) {
      const prev = ctx.getImageData(0, 0, canvas.width || 1, canvas.height || 1);
      canvas.width = nextW;
      canvas.height = nextH;
      ctx.putImageData(prev, 0, 0);
    }
    if (preview.width !== nextW || preview.height !== nextH) {
      preview.width = nextW;
      preview.height = nextH;
    }
    applyBrush();
  };

  const start = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    drawing = true;
    applyBrush();
    const p = point(e);
    lastX = p.x;
    lastY = p.y;
    canvas.setPointerCapture?.(e.pointerId);
    updatePreview(e);
    e.preventDefault();
  };

  const move = (e) => {
    if (!drawing) return;
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastX = p.x;
    lastY = p.y;
    e.preventDefault();
  };

  const end = () => {
    drawing = false;
  };

  const onSliderInput = () => {
    size = Number(slider.value) || 12;
    applyBrush();
  };

  const onSwatchClick = (btn) => {
    color = btn.dataset.color || '#111111';
    mode = 'pen';
    swatches.forEach((item) => item.classList.toggle('is-active', item === btn));
    modes.forEach((item) => item.classList.toggle('is-active', item.dataset.mode === 'pen'));
    applyBrush();
  };

  const onModeClick = (btn) => {
    mode = btn.dataset.mode || 'pen';
    modes.forEach((item) => item.classList.toggle('is-active', item === btn));
    applyBrush();
  };

  const onClear = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const onCanvasMove = (e) => {
    updatePreview(e);
    move(e);
  };

  const onDocMove = (e) => {
    updatePreview(e);
    if (drawing) move(e);
  };

  const onCanvasLeave = () => {
    end();
    clearPreview();
  };

  const swatchHandlers = [];
  const modeHandlers = [];

  slider?.addEventListener('input', onSliderInput);
  swatches.forEach((btn) => {
    const handler = () => onSwatchClick(btn);
    swatchHandlers.push([btn, handler]);
    btn.addEventListener('click', handler);
  });
  modes.forEach((btn) => {
    const handler = () => onModeClick(btn);
    modeHandlers.push([btn, handler]);
    btn.addEventListener('click', handler);
  });
  clearBtn?.addEventListener('click', onClear);
  window.addEventListener('resize', resize);
  canvas.addEventListener('pointerdown', start);
  canvas.addEventListener('pointermove', onCanvasMove);
  canvas.addEventListener('pointerup', end);
  canvas.addEventListener('pointercancel', end);
  canvas.addEventListener('pointerleave', onCanvasLeave);
  document.addEventListener('pointermove', onDocMove);

  resize();
  applyBrush();

  const destroy = () => {
    slider?.removeEventListener('input', onSliderInput);
    swatchHandlers.forEach(([btn, handler]) => btn.removeEventListener('click', handler));
    modeHandlers.forEach(([btn, handler]) => btn.removeEventListener('click', handler));
    clearBtn?.removeEventListener('click', onClear);
    window.removeEventListener('resize', resize);
    canvas.removeEventListener('pointerdown', start);
    canvas.removeEventListener('pointermove', onCanvasMove);
    canvas.removeEventListener('pointerup', end);
    canvas.removeEventListener('pointercancel', end);
    canvas.removeEventListener('pointerleave', onCanvasLeave);
    document.removeEventListener('pointermove', onDocMove);
    root.removeAttribute(ART_INIT_ATTR);
    instances.delete(root);
  };

  instances.set(root, destroy);
}

function destroyArtPage(root) {
  const destroy = instances.get(root);
  if (destroy) destroy();
}

function findArtPage(node) {
  if (!node || !node.querySelector) return null;
  return node.querySelector('.art-page') || (node.classList?.contains('art-page') ? node : null);
}

function bootArtPages() {
  document.querySelectorAll('.art-page').forEach(initArtPage);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootArtPages);
} else {
  bootArtPages();
}

document.addEventListener('shopify:section:load', (event) => {
  const root = findArtPage(event.target);
  if (root) initArtPage(root);
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = findArtPage(event.target);
  if (root) destroyArtPage(root);
});
